// Mirrors the relay.companies schema (see
// supabase/migrations/20260523120000_relay_companies.sql
// + 20260601130000_relay_unified_contact_company_stage.sql).

import { STAGES, type Stage } from "@/lib/stages";
import type { LeadType } from "@/lib/lead-type";

// Companies and contacts share one unified lifecycle "stage" set
// (see @/lib/stages and the relay.contacts/companies status CHECK constraints).
export const COMPANY_STATUSES = STAGES;
export type CompanyStatus = Stage;

// Mirrors the live relay.contacts / relay.companies source CHECK set
// (incl. 'document', added to contacts by Documents v1).
export const COMPANY_SOURCES = [
  "manual",
  "import",
  "booking",
  "social",
  "api",
  "document",
] as const;
// Free text since 20260601150000 — a built-in token above or an org-defined
// custom source from relay.lead_sources.
export type CompanySource = string;

export interface Company {
  id: string;
  organization_id: string;

  name: string;
  website: string | null;
  phone: string | null;
  email: string | null;
  industry: string | null;

  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;

  owner_id: string | null;

  status: CompanyStatus;
  // B2B (business account, the default) vs B2C (e.g. a sole proprietor record).
  lead_type: LeadType;
  // The account's main person — references relay.contacts(id).
  primary_contact_id: string | null;
  // The Forge customer org this company represents. Set only via
  // public.link_company_to_forge_org / public.unlink_company_from_forge_org;
  // direct UPDATE is blocked by a trigger. Surfaces the Forge Subscription card.
  platform_organization_id: string | null;
  source: CompanySource;
  lead_score: number | null;

  notes: string | null;
  metadata: Record<string, unknown>;

  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Subset of Company writable from a form. */
export type CompanyInput = Partial<
  Pick<
    Company,
    | "name"
    | "website"
    | "phone"
    | "email"
    | "industry"
    | "address_line1"
    | "address_line2"
    | "address_city"
    | "address_state"
    | "address_postal_code"
    | "address_country"
    | "owner_id"
    | "status"
    | "lead_type"
    | "primary_contact_id"
    | "source"
    | "lead_score"
    | "notes"
  >
>;

export function companyDisplayName(c: Pick<Company, "name">): string {
  return c.name?.trim() || "Unnamed company";
}
